import { useState } from "react";
import { Card, Button, Table, Tag, Modal, Form, Input, InputNumber, DatePicker, message, Empty, Tabs } from "antd";
import type { TabsProps } from "antd";
import dayjs from "dayjs";
import {
  useOpenRoutes,
  useMyRoutes,
  useCreateRoute,
  useBookRoute,
  useUpdateBooking,
  useOpenReferrals,
  useMyReferrals,
  useCreateReferral,
  useClaimReferral,
} from "~/services/network";
import type { TNetworkRoute, TJobReferral } from "~/services/network";
import { NetworkRouteStatus, NetworkBookingStatus, JobReferralStatus } from "~/utils/constants";

const statusColors: Record<string, string> = {
  OPEN: "green",
  FULL: "orange",
  COMPLETED: "blue",
  CANCELLED: "red",
  PENDING: "gold",
  ACCEPTED: "green",
  REJECTED: "red",
  CLAIMED: "cyan",
  DECLINED: "red",
};

const ShuttleRoutes = () => {
  const { data: openRoutes = [], isLoading: loadingOpen } = useOpenRoutes();
  const { data: myRoutes = [], isLoading: loadingMine } = useMyRoutes();
  const createRoute = useCreateRoute();
  const bookRoute = useBookRoute();
  const updateBooking = useUpdateBooking();
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [form] = Form.useForm();

  const handleCreate = async (values: any) => {
    try {
      await createRoute.mutateAsync({
        ...values,
        departureDate: values.departureDate.toISOString(),
        returnDate: values.returnDate?.toISOString(),
      });
      message.success("Route posted to network");
      setShowCreateModal(false);
      form.resetFields();
    } catch {
      message.error("Failed to create route");
    }
  };

  const handleBook = async (routeId: string) => {
    try {
      await bookRoute.mutateAsync({ routeId, slotsRequested: 1 });
      message.success("Booking request sent");
    } catch (e: any) {
      message.error(e?.response?.data?.message || "Failed to book");
    }
  };

  const handleBookingAction = async (routeId: string, bookingId: string, status: string) => {
    try {
      await updateBooking.mutateAsync({ routeId, bookingId, status });
      message.success(`Booking ${status.toLowerCase()}`);
    } catch {
      message.error("Failed to update booking");
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-lg font-semibold">Shuttle Routes</h2>
        <Button type="primary" onClick={() => setShowCreateModal(true)}
          className="bg-black hover:bg-gray-800 border-black">
          Post a Route
        </Button>
      </div>

      <Card title="Available Routes from Network" size="small">
        {loadingOpen ? (
          <div className="text-center py-8">Loading...</div>
        ) : openRoutes.length === 0 ? (
          <Empty description="No open routes from other companies" />
        ) : (
          <Table
            dataSource={openRoutes}
            rowKey="id"
            size="small"
            pagination={false}
            columns={[
              { title: "From", dataIndex: ["organization", "name"], key: "org" },
              { title: "Origin", dataIndex: "origin", key: "origin" },
              { title: "Destination", dataIndex: "destination", key: "dest" },
              {
                title: "Departure",
                dataIndex: "departureDate",
                key: "date",
                render: (d: string) => dayjs(d).format("MMM D, YYYY"),
              },
              { title: "Slots", dataIndex: "availableCapacity", key: "cap" },
              {
                title: "Price/Slot",
                dataIndex: "pricePerSlot",
                key: "price",
                render: (p: number) => (p ? `$${p}` : "—"),
              },
              {
                title: "",
                key: "action",
                render: (_: any, row: TNetworkRoute) => (
                  <Button size="small" onClick={() => handleBook(row.id)}>
                    Request Slot
                  </Button>
                ),
              },
            ]}
          />
        )}
      </Card>

      <Card title="My Posted Routes" size="small">
        {loadingMine ? (
          <div className="text-center py-8">Loading...</div>
        ) : myRoutes.length === 0 ? (
          <Empty description="You haven't posted any routes yet" />
        ) : (
          <Table
            dataSource={myRoutes}
            rowKey="id"
            size="small"
            pagination={false}
            columns={[
              { title: "Origin", dataIndex: "origin", key: "origin" },
              { title: "Destination", dataIndex: "destination", key: "dest" },
              {
                title: "Departure",
                dataIndex: "departureDate",
                key: "date",
                render: (d: string) => dayjs(d).format("MMM D, YYYY"),
              },
              { title: "Capacity", dataIndex: "availableCapacity", key: "cap" },
              {
                title: "Status",
                dataIndex: "status",
                key: "status",
                render: (s: string) => <Tag color={statusColors[s]}>{s}</Tag>,
              },
              {
                title: "Bookings",
                key: "bookings",
                render: (_: any, row: TNetworkRoute) =>
                  (row.bookings || []).map((b) => (
                    <div key={b.id} className="flex items-center gap-2 mb-1">
                      <span className="text-xs">{b.requestingOrg?.name}</span>
                      <Tag color={statusColors[b.status]} className="text-xs">
                        {b.status}
                      </Tag>
                      {b.status === "PENDING" && (
                        <div className="flex gap-1">
                          <Button
                            size="small"
                            type="link"
                            className="text-xs p-0"
                            onClick={() => handleBookingAction(row.id, b.id, NetworkBookingStatus.ACCEPTED)}
                          >
                            Accept
                          </Button>
                          <Button
                            size="small"
                            type="link"
                            danger
                            className="text-xs p-0"
                            onClick={() => handleBookingAction(row.id, b.id, NetworkBookingStatus.REJECTED)}
                          >
                            Reject
                          </Button>
                        </div>
                      )}
                    </div>
                  )),
              },
            ]}
          />
        )}
      </Card>

      <Modal
        title="Post a Shuttle Route"
        open={showCreateModal}
        onCancel={() => setShowCreateModal(false)}
        footer={null}
      >
        <Form form={form} layout="vertical" onFinish={handleCreate}>
          <div className="grid grid-cols-2 gap-4">
            <Form.Item name="origin" label="Origin" rules={[{ required: true }]}>
              <Input placeholder="e.g. Brooklyn, NY" />
            </Form.Item>
            <Form.Item name="destination" label="Destination" rules={[{ required: true }]}>
              <Input placeholder="e.g. Miami, FL" />
            </Form.Item>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <Form.Item name="departureDate" label="Departure" rules={[{ required: true }]}>
              <DatePicker className="w-full" />
            </Form.Item>
            <Form.Item name="returnDate" label="Return (optional)">
              <DatePicker className="w-full" />
            </Form.Item>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <Form.Item name="availableCapacity" label="Available Slots" rules={[{ required: true }]}>
              <InputNumber min={1} className="w-full" />
            </Form.Item>
            <Form.Item name="pricePerSlot" label="Price per Slot ($)">
              <InputNumber min={0} className="w-full" />
            </Form.Item>
          </div>
          <Button type="primary" htmlType="submit" loading={createRoute.isPending} block
            className="bg-black hover:bg-gray-800 border-black">
            Post Route
          </Button>
        </Form>
      </Modal>
    </div>
  );
};

const JobReferrals = () => {
  const { data: openReferrals = [], isLoading: loadingOpen } = useOpenReferrals();
  const { data: myReferrals = [], isLoading: loadingMine } = useMyReferrals();
  const createReferral = useCreateReferral();
  const claimReferral = useClaimReferral();
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [form] = Form.useForm();

  const handleCreate = async (values: any) => {
    try {
      await createReferral.mutateAsync(values);
      message.success("Referral posted");
      setShowCreateModal(false);
      form.resetFields();
    } catch {
      message.error("Failed to create referral");
    }
  };

  const handleClaim = async (id: string) => {
    try {
      await claimReferral.mutateAsync(id);
      message.success("Referral claimed");
    } catch (e: any) {
      message.error(e?.response?.data?.message || "Failed to claim");
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-lg font-semibold">Job Referrals</h2>
        <Button type="primary" onClick={() => setShowCreateModal(true)}
          className="bg-black hover:bg-gray-800 border-black">
          Post a Referral
        </Button>
      </div>

      <Card title="Available Referrals" size="small">
        {loadingOpen ? (
          <div className="text-center py-8">Loading...</div>
        ) : openReferrals.length === 0 ? (
          <Empty description="No open referrals available" />
        ) : (
          <Table
            dataSource={openReferrals}
            rowKey="id"
            size="small"
            pagination={false}
            columns={[
              { title: "From", dataIndex: ["sourceOrg", "name"], key: "source" },
              { title: "Description", dataIndex: "description", key: "desc" },
              {
                title: "Fee",
                dataIndex: "referralFee",
                key: "fee",
                render: (f: number) => (f ? `$${f}` : "—"),
              },
              {
                title: "Posted",
                dataIndex: "createdAt",
                key: "date",
                render: (d: string) => dayjs(d).format("MMM D"),
              },
              {
                title: "",
                key: "action",
                render: (_: any, row: TJobReferral) => (
                  <Button size="small" onClick={() => handleClaim(row.id)}>
                    Claim Job
                  </Button>
                ),
              },
            ]}
          />
        )}
      </Card>

      <Card title="My Posted Referrals" size="small">
        {loadingMine ? (
          <div className="text-center py-8">Loading...</div>
        ) : myReferrals.length === 0 ? (
          <Empty description="You haven't posted any referrals" />
        ) : (
          <Table
            dataSource={myReferrals}
            rowKey="id"
            size="small"
            pagination={false}
            columns={[
              { title: "Description", dataIndex: "description", key: "desc" },
              {
                title: "Status",
                dataIndex: "status",
                key: "status",
                render: (s: string) => <Tag color={statusColors[s]}>{s}</Tag>,
              },
              {
                title: "Claimed By",
                dataIndex: ["claimedByOrg", "name"],
                key: "claimed",
                render: (n: string) => n || "—",
              },
              {
                title: "Fee",
                dataIndex: "referralFee",
                key: "fee",
                render: (f: number) => (f ? `$${f}` : "—"),
              },
            ]}
          />
        )}
      </Card>

      <Modal
        title="Post a Job Referral"
        open={showCreateModal}
        onCancel={() => setShowCreateModal(false)}
        footer={null}
      >
        <Form form={form} layout="vertical" onFinish={handleCreate}>
          <Form.Item name="description" label="Job Description" rules={[{ required: true }]}>
            <Input.TextArea rows={3} placeholder="Describe the job you need to refer..." />
          </Form.Item>
          <Form.Item name="referralFee" label="Referral Fee ($, optional)">
            <InputNumber min={0} className="w-full" />
          </Form.Item>
          <Button type="primary" htmlType="submit" loading={createReferral.isPending} block
            className="bg-black hover:bg-gray-800 border-black">
            Post Referral
          </Button>
        </Form>
      </Modal>
    </div>
  );
};

export const NetworkHub = () => {
  const items: TabsProps["items"] = [
    { key: "shuttles", label: "Shuttle Routes", children: <ShuttleRoutes /> },
    { key: "referrals", label: "Job Referrals", children: <JobReferrals /> },
  ];

  return (
    <div className="p-6">
      <h1 className="text-xl font-bold mb-4">Network</h1>
      <p className="text-sm text-gray-500 mb-6">
        Share shuttle capacity and refer jobs to other art handling companies on the platform.
      </p>
      <Tabs items={items} />
    </div>
  );
};
